/**
 * Subscription API Client — Recurring Wallet Top-Up
 *
 * Fetch current recurring top-up info, create/update/cancel recurring top-ups,
 * and open the Stripe Customer Portal.
 */

import { apiCall } from './api-helpers';

export interface RecurringTopUpData {
  id: string;
  status: 'ACTIVE' | 'CANCELED' | 'PAST_DUE' | 'UNPAID' | 'INCOMPLETE';
  /** Monthly recurring amount in USD cents (e.g. 2500 = $25/mo) */
  recurringAmount: number;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
}

export interface SubscriptionResponse {
  subscription: RecurringTopUpData | null;
  stripeCustomerId: string | null;
}

export interface CheckoutResponse {
  checkoutUrl: string;
}

export interface PortalResponse {
  portalUrl: string;
}

export interface UpdateResponse {
  subscription: {
    id: string;
    status: string;
    recurringAmount: number;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
  };
}

export const subscriptionAPI = {
  /**
   * Get the current user's recurring top-up info (or null if none).
   */
  async getSubscription(isAuthenticated: boolean): Promise<SubscriptionResponse> {
    const result = await apiCall('/api/subscription', isAuthenticated) as {
      success: boolean;
      data: SubscriptionResponse;
    };
    return result.data;
  },

  /**
   * Create a new recurring top-up via Stripe Checkout.
   * Returns the checkout URL for redirect.
   */
  async createRecurringTopUp(monthlyAmount: number, isAuthenticated: boolean): Promise<CheckoutResponse> {
    const result = await apiCall('/api/subscription/create', isAuthenticated, {
      method: 'POST',
      body: JSON.stringify({ monthlyAmount }),
    }) as { success: boolean; data: CheckoutResponse };
    return result.data;
  },

  /**
   * Update the monthly recurring amount.
   */
  async updateAmount(monthlyAmount: number, isAuthenticated: boolean): Promise<UpdateResponse> {
    const result = await apiCall('/api/subscription/update', isAuthenticated, {
      method: 'POST',
      body: JSON.stringify({ monthlyAmount }),
    }) as { success: boolean; data: UpdateResponse };
    return result.data;
  },

  /**
   * Cancel recurring top-up at end of current billing period.
   */
  async cancelRecurringTopUp(isAuthenticated: boolean): Promise<UpdateResponse> {
    const result = await apiCall('/api/subscription/cancel', isAuthenticated, {
      method: 'POST',
    }) as { success: boolean; data: UpdateResponse };
    return result.data;
  },

  /**
   * Resume a recurring top-up that was scheduled for cancellation.
   */
  async resumeRecurringTopUp(isAuthenticated: boolean): Promise<UpdateResponse> {
    const result = await apiCall('/api/subscription/resume', isAuthenticated, {
      method: 'POST',
    }) as { success: boolean; data: UpdateResponse };
    return result.data;
  },

  /**
   * Get a Stripe Customer Portal URL for managing billing details.
   */
  async getPortalUrl(isAuthenticated: boolean): Promise<PortalResponse> {
    const result = await apiCall('/api/subscription/portal', isAuthenticated) as {
      success: boolean;
      data: PortalResponse;
    };
    return result.data;
  },
};
